import type { GetterTree, Store } from "vuex";
import type { State } from "@/store/state";
import type { SiteConfigState } from "./state";
import type { SiteConfig } from "@/interfaces/site_config";
import type { MapboxLayersFixed } from "@/interfaces/gis_layer";

export interface MapBaseLayer {
  style: string;
  layers: Partial<MapboxLayersFixed>[];
}

export const getters: GetterTree<SiteConfigState, State> = {
  siteConfigurations: (state): SiteConfig[] => state.siteConfigurations,
  siteConfigurationsLoaded: (state): boolean => state.siteConfigurationsLoaded,
  siteConfigTimeZone: (state): string => state.projectTimeZone,
  siteConfigTimeZoneLoaded: (state): boolean => state.projectTimeZoneLoaded,
  mapCenter: (state): { lat: number; lon: number } => {
    const center_lat = state.siteConfigurations.find(
      (config) => config.name === "center_lat",
    )?.val;
    const center_lon = state.siteConfigurations.find(
      (config) => config.name === "center_lon",
    )?.val;
    if (center_lat && center_lon) {
      return { lat: Number(center_lat), lon: Number(center_lon) };
    }
    return { lat: 0, lon: 0 };
  },
  mapZoom: (state): number => {
    const zoom = state.siteConfigurations.find(
      (config) => config.name === "zoom",
    )?.val;
    if (zoom) {
      return Number(zoom);
    }
    return 5;
  },
  demoMode: (state): boolean => state.demoMode,
  assuresrvVersion: (state): string | null => state.assuresrvVersion,
  spectrogramBaselineDays: (state): number => state.spectrogramBaselineDays,
  onPremMode: (state): boolean =>
    state.siteConfigurations.find((config) => config.name === "on_prem")
      ?.val === "true",
  geoserverMode: (state): boolean =>
    state.siteConfigurations.find(
      (config) => config.name === "geoserver_enabled",
    )?.val === "true",
  geoserverBaseLayer: (state): string | null =>
    state.siteConfigurations.find(
      (config) => config.name === "layer" && config.category === "base_map",
    )?.val ?? null,
  geoserverWorkspace: (state): string | null =>
    state.siteConfigurations.find(
      (config) => config.name === "workspace" && config.category === "base_map",
    )?.val ?? null,
  showSatellite: (state): boolean =>
    state.siteConfigurations.find(
      (config) => config.name === "show_satellite" && config.category === "map",
    )?.val === "true",
  mapBaseLayer: (_state, getters): MapBaseLayer => {
    const geoserverEnabled = getters.geoserverMode as boolean;
    const baseLayer = getters.geoserverBaseLayer as string | null;
    const workspace = getters.geoserverWorkspace as string | null;
    const showSatellite = getters.showSatellite as boolean;

    if (geoserverEnabled && baseLayer && workspace) {
      const baseUrl = `${window.location.protocol}//${
        window.location.host.split(":")[0]
      }`;
      return {
        style: "white-bg",
        layers: [
          {
            below: "traces",
            sourcetype: "raster",
            source: [
              `${baseUrl}/geoserver/${workspace}/wms?service=WMS&version=1.1.1&request=GetMap&layers=${workspace}%3A${baseLayer}&bbox={bbox-epsg-3857}` +
                "&CRS=EPSG:3857&styles=&format=image/png8&WIDTH=256&HEIGHT=256",
            ],
          },
        ],
      };
    }

    return {
      style: showSatellite ? "satellite-streets" : "light",
      layers: [],
    };
  },
};

const NS = "siteConfig/";

export const readSiteConfigurations = (store: Store<State>) =>
  store.getters[`${NS}siteConfigurations`] as SiteConfig[];
export const readSiteConfigurationsLoaded = (store: Store<State>) =>
  store.getters[`${NS}siteConfigurationsLoaded`] as boolean;
export const readSiteTimeZone = (store: Store<State>) =>
  store.getters[`${NS}siteConfigTimeZone`] as string;
export const readSiteTimeZoneLoaded = (store: Store<State>) =>
  store.getters[`${NS}siteConfigTimeZoneLoaded`] as boolean;
export const readSiteMapCenter = (store: Store<State>) =>
  store.getters[`${NS}mapCenter`] as { lat: number; lon: number };
export const readConfiguredMapZoom = (store: Store<State>) =>
  store.getters[`${NS}mapZoom`] as number;
export const readDemoMode = (store: Store<State>) =>
  store.getters[`${NS}demoMode`] as boolean;
export const readAssuresrvVersion = (store: Store<State>) =>
  store.getters[`${NS}assuresrvVersion`] as string | null;
export const readSpectrogramBaselineDays = (store: Store<State>) =>
  store.getters[`${NS}spectrogramBaselineDays`] as number;
export const readOnPremMode = (store: Store<State>) =>
  store.getters[`${NS}onPremMode`] as boolean;
export const readGeoserverMode = (store: Store<State>) =>
  store.getters[`${NS}geoserverMode`] as boolean;
export const readGeoserverBaseLayer = (store: Store<State>) =>
  store.getters[`${NS}geoserverBaseLayer`] as string | null;
export const readGeoserverWorkspace = (store: Store<State>) =>
  store.getters[`${NS}geoserverWorkspace`] as string | null;
export const readShowSatellite = (store: Store<State>) =>
  store.getters[`${NS}showSatellite`] as boolean;
export const readMapBaseLayer = (store: Store<State>) =>
  store.getters[`${NS}mapBaseLayer`] as MapBaseLayer;
